import html2canvas from "html2canvas";

/**
 * Captures a screenshot of the current web screen context.
 * Leverages html2canvas with scale optimization.
 * Returns a base64 Data URL (image/jpeg) or null if unavailable.
 *
 * @returns {Promise<string | null>} Base64 image data URL or null.
 */
export async function captureCurrentScreen(): Promise<string | null> {
  try {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return null;
    }

    const target = document.getElementById("root") || document.body;
    if (!target) return null;

    const canvas = await html2canvas(target, {
      logging: false,
      useCORS: true,
      scale: 0.35, // Compact thumbnail to prevent memory and payload bloat
      backgroundColor: "#FAF8F5", // Archival warm background default
      ignoreElements: (element) => {
        // Ignore the feedback modal itself if open
        return element.getAttribute("data-feedback-modal") === "true";
      },
    });

    return canvas.toDataURL("image/jpeg", 0.5);
  } catch (err) {
    console.warn("[ScreenCapture] Failed to capture web screenshot:", err);
    return null;
  }
}

/**
 * Checks whether screen capture or interactive screenshot attachment is available on this platform.
 *
 * @returns {boolean} True on browser DOM environments.
 */
export function isScreenCaptureAvailable(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

/**
 * Fallback device picker for web — returns null or prompts file picker.
 *
 * @returns {Promise<string | null>} Always delegates to captureCurrentScreen on web.
 */
export async function attachScreenshotFromDevice(): Promise<string | null> {
  return captureCurrentScreen();
}

